/**
 * CalculatorPanel — the contract value calculator (plan §10.5, §11 tab 2
 * "Inside each negotiation, two panels ... Calculator: the contract value
 * calculator (10.5) with saved scenarios, its inputs taken from the
 * selected version", §13.8 WP-73, R31).
 *
 * ORCHESTRATOR DECISION (session 4; see this WP's report, marked there as a
 * deliberate deviation from §13.8's own wording): §13.8's WP-73 line says
 * this WP owns `frontend/src/contracts/tabs/studies/calculator/**`. It does
 * NOT, this wave — that whole path is WP-34's (done, committed), and the
 * Calculator slot lives inside WP-34's `NegotiationModal.tsx`, a file this
 * WP must not touch (§13.12's one-WP-per-file rule). This panel is built
 * here instead, beside `components/notesPanel/**` (WP-70) — the same shape
 * of thing, a panel another WP's tab mounts — and the one-line slot edit
 * that wires it in is written into `calculator.mount.md` for the deferred
 * MOUNT step (this component is NOT reachable in the running app until that
 * paste lands; it is reachable only through this WP's own
 * `__tests__/calculator.test.tsx` today).
 *
 * No version picker here (§10.5 FACTS 8 — WP-74's Versions panel owns
 * choosing a version; this panel shows which one the inputs came from and
 * runs against the negotiation's own final-or-offered version).
 *
 * Design: .claude/skills/siteoperations-design/SKILL.md (v2 Compact
 * Professional) — card/panel recipe, fieldCls inputs, SegmentedControl,
 * status-pill G8 labels, dark: on every surface.
 */
import { useCallback, useEffect, useState, type FormEvent } from 'react';
import { Calculator, Loader2, AlertTriangle, Save, ChevronDown, ChevronRight } from 'lucide-react';
// Specific-file imports, not the `ui` barrel: the barrel re-exports
// StudyPicker.tsx, which imports services/api -> axios (ESM), and this
// repo's jest cannot parse axios. This panel takes everything through props
// (no ContractsProvider) and its test therefore never mocks services/api —
// the same convention FeesPanel.tsx and VersionsPanel.tsx use (see
// tabs/rateCard/outcomes/fees/FeesPanel.tsx's `ui/EmptyState` import and
// components/versionsPanel/VersionsPanel.tsx's `ui/EmptyState`, `ui/Modal`,
// `ui/Toast` imports).
import { fieldCls } from '../../../ui/styles';
import SegmentedControl from '../../../ui/SegmentedControl';
import EmptyState from '../../../ui/EmptyState';
import {
  runCalculator as runCalculatorApi,
  listScenarios,
  saveScenario,
  type CalculatorInputs,
  type CalculatorScenario,
  type PaymentFrequency,
  type RunCalculatorResult,
} from '../../api/calculator';

export interface CalculatorPanelProps {
  negotiationId: string;
  studyId: string;
  className?: string;
}

/** The editable form draft — a fraction-valued field is edited as a PERCENT (0-100) for readability, converted at the API boundary. */
interface Draft {
  enrollmentTarget: string;
  screenFailRatePct: string;
  assumeAllVisitsAttended: boolean;
  visitsAttendedPct: string;
  assumeInvoiceablesDone: boolean;
  paymentFrequency: PaymentFrequency;
  paymentDays: string;
  holdbackPct: string;
  studyMonths: string;
  enrollmentMonths: string;
}

const DEFAULT_DRAFT: Draft = {
  enrollmentTarget: '20',
  screenFailRatePct: '25',
  assumeAllVisitsAttended: true,
  visitsAttendedPct: '100',
  assumeInvoiceablesDone: true,
  paymentFrequency: 'monthly',
  paymentDays: '30',
  holdbackPct: '10',
  studyMonths: '12',
  enrollmentMonths: '12',
};

const LABEL_PILL_CLS: Record<string, string> = {
  derived: 'bg-gray-100 text-gray-700 dark:bg-gray-700 dark:text-gray-300',
  inferred: 'bg-amber-100 text-amber-800 dark:bg-amber-900/40 dark:text-amber-300',
  extracted: 'bg-primary-50 text-primary-700 dark:bg-primary-900/20 dark:text-primary-400',
};

function toNumber(text: string): number | undefined {
  if (text.trim() === '') return undefined;
  const n = Number(text);
  return Number.isFinite(n) ? n : undefined;
}

function draftToOverrides(draft: Draft): CalculatorInputs {
  const overrides: CalculatorInputs = {
    enrollment_target: toNumber(draft.enrollmentTarget),
    screen_fail_rate: toNumber(draft.screenFailRatePct) !== undefined ? Number(draft.screenFailRatePct) / 100 : undefined,
    assume_all_visits_attended: draft.assumeAllVisitsAttended,
    visits_attended_fraction: toNumber(draft.visitsAttendedPct) !== undefined ? Number(draft.visitsAttendedPct) / 100 : undefined,
    assume_invoiceables_done: draft.assumeInvoiceablesDone,
    payment_frequency: draft.paymentFrequency,
    payment_days: toNumber(draft.paymentDays),
    holdback_pct: toNumber(draft.holdbackPct) !== undefined ? Number(draft.holdbackPct) / 100 : undefined,
    study_months: toNumber(draft.studyMonths),
    enrollment_months: toNumber(draft.enrollmentMonths),
  };
  return overrides;
}

function money(value: number | null | undefined): string {
  if (value === null || value === undefined || !Number.isFinite(value)) return '—';
  return value.toLocaleString('en-US', { style: 'currency', currency: 'USD', maximumFractionDigits: 2 });
}

/** A labelled output value with its G8 pill and a hover title carrying the source (§11 Global, G7, G8). */
function OutputStat({
  testId,
  title,
  value,
  label,
  hoverTitle,
}: {
  testId: string;
  title: string;
  value: string;
  label?: string;
  hoverTitle?: string;
}) {
  return (
    <div
      data-testid={testId}
      title={hoverTitle}
      className="rounded-lg border border-gray-200 p-3 dark:border-gray-700"
    >
      <div className="flex items-center justify-between gap-2">
        <span className="text-xs font-medium uppercase tracking-wide text-gray-400 dark:text-gray-500">{title}</span>
        {label && (
          <span className={`inline-flex items-center rounded-full px-2 py-0.5 text-[10px] font-medium ${LABEL_PILL_CLS[label] ?? LABEL_PILL_CLS.derived}`}>
            {label}
          </span>
        )}
      </div>
      <p className="mt-1 text-lg font-semibold tabular-nums text-gray-900 dark:text-gray-100">{value}</p>
    </div>
  );
}

export function CalculatorPanel({ negotiationId, className }: CalculatorPanelProps) {
  const [draft, setDraft] = useState<Draft>(DEFAULT_DRAFT);
  const [result, setResult] = useState<RunCalculatorResult | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const [scenarios, setScenarios] = useState<CalculatorScenario[]>([]);
  const [scenarioName, setScenarioName] = useState('');
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);
  const [cashFlowExpanded, setCashFlowExpanded] = useState(false);

  const run = useCallback(async (nextDraft: Draft) => {
    if (!negotiationId) return;
    setLoading(true);
    setError(null);
    try {
      const overrides = draftToOverrides(nextDraft);
      const response = await runCalculatorApi({ negotiationId, overrides });
      setResult(response);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to run the calculator');
    } finally {
      setLoading(false);
    }
  }, [negotiationId]);

  const loadScenarios = useCallback(async () => {
    if (!negotiationId) return;
    try {
      const list = await listScenarios(negotiationId);
      setScenarios(list);
    } catch {
      // The saved-scenario list is a convenience, not the panel's primary
      // job — a failure here does not block running or saving.
    }
  }, [negotiationId]);

  useEffect(() => {
    run(draft);
    loadScenarios();
    // Only on mount / negotiation change — every draft edit below re-runs
    // explicitly through `update`, so `draft` itself is deliberately NOT a
    // dependency here (that would re-run twice per keystroke).
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [negotiationId]);

  /** Every field is fully LOCAL state (value from `draft`, never from the fetched response) — a debounce-free controlled input that never waits on the network to show what was typed. */
  function update<K extends keyof Draft>(key: K, value: Draft[K]) {
    const next = { ...draft, [key]: value };
    setDraft(next);
    run(next);
  }

  async function handleSave(e: FormEvent) {
    e.preventDefault();
    const name = scenarioName.trim();
    if (!name) return;
    setSaving(true);
    setSaveError(null);
    try {
      const overrides = draftToOverrides(draft);
      const scenario = await saveScenario({ negotiationId, name, overrides });
      setScenarios((prev) => [scenario, ...prev]);
      setScenarioName('');
    } catch (err) {
      setSaveError(err instanceof Error ? err.message : 'Failed to save the scenario');
    } finally {
      setSaving(false);
    }
  }

  const outputs = result?.outputs;
  const warnings = result?.warnings ?? [];
  const unpriced = result?.unpriced ?? [];
  const termDisagreements = result?.term_disagreements ?? [];
  const hasWarnings = warnings.length > 0 || unpriced.length > 0 || termDisagreements.length > 0;

  return (
    <div data-testid="calculator-panel" className={`flex flex-col gap-4 ${className ?? ''}`}>
      <div className="flex items-center gap-2 text-sm text-gray-500 dark:text-gray-400">
        <Calculator size={16} className="text-primary-600 dark:text-primary-400" />
        {result?.version_id ? (
          <span data-testid="calc-version-info">
            Inputs from version <span className="font-mono text-xs">{result.version_id}</span> — the negotiation&rsquo;s final or
            offered version (§10.1). Choose a different version in the Versions panel.
          </span>
        ) : (
          <span data-testid="calc-version-info">No budget version selected yet for this negotiation.</span>
        )}
        {loading && <Loader2 size={14} className="animate-spin" />}
      </div>

      {error && (
        <div className="rounded-lg border border-red-200 bg-red-50 px-2.5 py-1.5 text-sm text-red-700 dark:border-red-800 dark:bg-red-900/20 dark:text-red-400">
          {error}
        </div>
      )}

      {/* -------------------------------------------------------------- Inputs */}
      <div className="grid grid-cols-2 gap-3 sm:grid-cols-3">
        <label className="flex flex-col gap-1 text-xs font-medium text-gray-500 dark:text-gray-400">
          Enrollment target
          <input
            data-testid="calc-input-enrollment-target"
            type="number"
            min={0}
            className={fieldCls}
            value={draft.enrollmentTarget}
            onChange={(e) => update('enrollmentTarget', e.target.value)}
          />
        </label>
        <label className="flex flex-col gap-1 text-xs font-medium text-gray-500 dark:text-gray-400">
          Screen-fail rate (%)
          <input
            data-testid="calc-input-screen-fail-rate"
            type="number"
            min={0}
            max={99}
            className={fieldCls}
            value={draft.screenFailRatePct}
            onChange={(e) => update('screenFailRatePct', e.target.value)}
          />
        </label>
        <label className="flex flex-col gap-1 text-xs font-medium text-gray-500 dark:text-gray-400">
          Payment frequency
          <SegmentedControl
            label="Payment frequency"
            options={[
              { value: 'monthly', label: 'Monthly' },
              { value: 'quarterly', label: 'Quarterly' },
            ]}
            value={draft.paymentFrequency}
            onChange={(v) => update('paymentFrequency', v as PaymentFrequency)}
          />
        </label>

        <label className="flex flex-col gap-1 text-xs font-medium text-gray-500 dark:text-gray-400">
          <span className="flex items-center gap-1.5">
            <input
              data-testid="calc-toggle-assume-all-visits"
              type="checkbox"
              checked={draft.assumeAllVisitsAttended}
              onChange={(e) => update('assumeAllVisitsAttended', e.target.checked)}
            />
            Assume all visits attended
          </span>
        </label>
        <label className="flex flex-col gap-1 text-xs font-medium text-gray-500 dark:text-gray-400">
          Visits attended (%)
          <input
            data-testid="calc-input-visits-attended"
            type="number"
            min={0}
            max={100}
            disabled={draft.assumeAllVisitsAttended}
            className={`${fieldCls} disabled:opacity-50`}
            value={draft.visitsAttendedPct}
            onChange={(e) => update('visitsAttendedPct', e.target.value)}
          />
        </label>
        <label className="flex flex-col gap-1 text-xs font-medium text-gray-500 dark:text-gray-400">
          <span className="flex items-center gap-1.5">
            <input
              data-testid="calc-toggle-assume-invoiceables-done"
              type="checkbox"
              checked={draft.assumeInvoiceablesDone}
              onChange={(e) => update('assumeInvoiceablesDone', e.target.checked)}
            />
            Invoiceables assumed done
          </span>
        </label>

        <label className="flex flex-col gap-1 text-xs font-medium text-gray-500 dark:text-gray-400">
          Net days
          <input
            data-testid="calc-input-payment-days"
            type="number"
            min={0}
            className={fieldCls}
            value={draft.paymentDays}
            onChange={(e) => update('paymentDays', e.target.value)}
          />
        </label>
        <label className="flex flex-col gap-1 text-xs font-medium text-gray-500 dark:text-gray-400">
          Holdback (%)
          <input
            data-testid="calc-input-holdback-pct"
            type="number"
            min={0}
            max={100}
            className={fieldCls}
            value={draft.holdbackPct}
            onChange={(e) => update('holdbackPct', e.target.value)}
          />
        </label>
        <div />

        <label className="flex flex-col gap-1 text-xs font-medium text-gray-500 dark:text-gray-400">
          Study months
          <input
            data-testid="calc-input-study-months"
            type="number"
            min={1}
            className={fieldCls}
            value={draft.studyMonths}
            onChange={(e) => update('studyMonths', e.target.value)}
          />
        </label>
        <label className="flex flex-col gap-1 text-xs font-medium text-gray-500 dark:text-gray-400">
          Enrollment months
          <input
            data-testid="calc-input-enrollment-months"
            type="number"
            min={1}
            className={fieldCls}
            value={draft.enrollmentMonths}
            onChange={(e) => update('enrollmentMonths', e.target.value)}
          />
        </label>
      </div>

      {/* ------------------------------------------------------------- Outputs */}
      {outputs ? (
        <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-5">
          <OutputStat
            testId="calc-output-per-patient-revenue"
            title="Per-patient revenue"
            value={money(outputs.per_patient_revenue.value)}
            label={outputs.per_patient_revenue.label}
            hoverTitle={`per_patient_revenue_per_subject × enrollment_target (source: ${result?.sources.per_patient_total ?? 'default'})`}
          />
          <OutputStat
            testId="calc-output-screen-fail-allowance"
            title="Screen-fail allowance"
            value={money(outputs.screen_fail_allowance.value)}
            label={outputs.screen_fail_allowance.label}
            hoverTitle={`${outputs.screen_fails_expected.value} expected, ${outputs.screen_fail_limited_by.value === 'none' ? 'not limited' : `limited by ${outputs.screen_fail_limited_by.value}`}`}
          />
          <OutputStat
            testId="calc-output-fixed-fees"
            title="Fixed fees"
            value={money(outputs.fixed_fees.value)}
            label={outputs.fixed_fees.label}
            hoverTitle={`source: ${result?.sources.fees ?? 'default'}`}
          />
          <OutputStat
            testId="calc-output-invoiceables-total"
            title="Invoiceables"
            value={money(outputs.invoiceables_total.value)}
            label={outputs.invoiceables_total.label}
            hoverTitle={`source: ${result?.sources.invoiceables ?? 'default'}`}
          />
          <OutputStat
            testId="calc-output-total-contract-value"
            title="Total contract value"
            value={money(outputs.total_contract_value.value)}
            label={outputs.total_contract_value.label}
          />
        </div>
      ) : (
        !loading && <EmptyState icon={Calculator} title="No calculation yet" hint="Set the inputs above to run the calculator." />
      )}

      {/* ---------------------------------------------------------- Cash flow */}
      {outputs && outputs.cash_flow.value.length > 0 && (
        <div>
          <button
            type="button"
            data-testid="calc-cash-flow-toggle"
            onClick={() => setCashFlowExpanded((v) => !v)}
            className="flex items-center gap-1 text-xs font-semibold uppercase tracking-widest text-gray-400 hover:text-gray-600 dark:text-gray-500 dark:hover:text-gray-300"
          >
            {cashFlowExpanded ? <ChevronDown size={14} /> : <ChevronRight size={14} />}
            Month-by-month cash flow
          </button>
          {cashFlowExpanded && (
            <div className="mt-2 overflow-x-auto rounded-xl border border-gray-200 dark:border-gray-700">
              <table data-testid="calc-cash-flow-table" className="min-w-full divide-y divide-gray-200 text-sm dark:divide-gray-700">
                <thead className="bg-gray-50 dark:bg-gray-800/50">
                  <tr>
                    {['Month', 'Earned', 'Invoiced', 'Holdback withheld', 'Paid', 'Cumulative paid'].map((h) => (
                      <th key={h} className="px-3 py-2 text-left text-xs font-medium uppercase tracking-wider text-gray-500 dark:text-gray-400">
                        {h}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
                  {outputs.cash_flow.value.map((row) => (
                    <tr key={row.month}>
                      <td className="px-3 py-2 text-gray-900 dark:text-gray-100">{row.month}</td>
                      <td className="px-3 py-2 tabular-nums text-gray-900 dark:text-gray-100">{money(row.earned)}</td>
                      <td className="px-3 py-2 tabular-nums text-gray-900 dark:text-gray-100">{money(row.invoiced)}</td>
                      <td className="px-3 py-2 tabular-nums text-gray-900 dark:text-gray-100">{money(row.holdback_withheld)}</td>
                      <td className="px-3 py-2 tabular-nums text-gray-900 dark:text-gray-100">
                        {money(row.paid)}
                        {row.holdback_release !== undefined && (
                          <span className="ml-1 text-xs text-primary-600 dark:text-primary-400">
                            (incl. {money(row.holdback_release)} holdback release{row.holdback_release_trigger ? `: ${row.holdback_release_trigger}` : ''})
                          </span>
                        )}
                      </td>
                      <td className="px-3 py-2 tabular-nums text-gray-900 dark:text-gray-100">{money(row.cumulative_paid)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      {/* ----------------------------------------------------------- Warnings */}
      {hasWarnings && (
        <div
          data-testid="calc-warnings"
          className="rounded-lg border border-amber-200 bg-amber-50 p-3 text-sm text-amber-800 dark:border-amber-800 dark:bg-amber-900/20 dark:text-amber-300"
        >
          <div className="flex items-center gap-1.5 font-medium">
            <AlertTriangle size={14} /> Incomplete — nothing here is a silent zero
          </div>
          <ul className="mt-1.5 list-disc space-y-0.5 pl-5">
            {warnings.map((w) => (
              <li key={w}>{w.replace(/_/g, ' ')}</li>
            ))}
            {unpriced.map((label) => (
              <li key={`unpriced-${label}`}>Unpriced line, excluded from every total: &ldquo;{label}&rdquo;</li>
            ))}
            {termDisagreements.map((t, i) => (
              <li key={`disagree-${i}`}>
                A CTA term disagreed and was not used ({t.class}): &ldquo;{t.text}&rdquo;
              </li>
            ))}
          </ul>
        </div>
      )}

      {/* ------------------------------------------------------- Save scenario */}
      <form data-testid="calc-save-form" onSubmit={handleSave} className="flex flex-wrap items-end gap-2 border-t border-gray-200 pt-3 dark:border-gray-700">
        <label className="flex flex-1 min-w-[180px] flex-col gap-1 text-xs font-medium text-gray-500 dark:text-gray-400">
          Save this scenario as
          <input
            data-testid="calc-save-name-input"
            type="text"
            className={fieldCls}
            value={scenarioName}
            onChange={(e) => setScenarioName(e.target.value)}
            placeholder="e.g. Base case&hellip;"
          />
        </label>
        {saveError && <p className="text-xs text-red-600 dark:text-red-400">{saveError}</p>}
        <button
          type="submit"
          data-testid="calc-save-submit"
          disabled={saving || !scenarioName.trim()}
          className="inline-flex min-h-[44px] items-center gap-1.5 rounded-lg bg-primary-600 px-3 py-1.5 text-sm font-medium text-white transition-colors duration-150 hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-primary-500 disabled:cursor-not-allowed disabled:opacity-50 sm:min-h-0"
        >
          <Save size={16} /> Save scenario
        </button>
      </form>

      {scenarios.length > 0 && (
        <ul data-testid="calc-saved-scenarios-list" className="flex flex-col gap-1.5">
          {scenarios.map((s) => (
            <li
              key={s.id}
              data-testid={`calc-saved-scenario-${s.id}`}
              className="flex items-center justify-between rounded-lg border border-gray-200 px-2.5 py-1.5 text-sm dark:border-gray-700"
            >
              <span className="font-medium text-gray-900 dark:text-gray-100">{s.inputs.name}</span>
              <span className="tabular-nums text-gray-500 dark:text-gray-400">{money(s.outputs.total_contract_value?.value)}</span>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

export default CalculatorPanel;
